import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Optional,
    Output,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RadioGroupDirective } from './radio-group.directive';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

export class RadioChangeEvent {
    public source: RadioButtonComponent;
    public value: any;
    public checked: boolean = false;

    constructor(source: RadioButtonComponent, value: any, checked: boolean) {
        this.source = source;
        this.value = value;
        this.checked = checked;
    }
}

/* eslint-disable @angular-eslint/no-forward-ref */

// Counter for unique component ids.
let uniqueId = 0;

@Component({
    selector: 'ihd-radio-button',
    templateUrl: './radio-button.component.html',
    styleUrls: ['./radio-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '(click)': 'toggle()',
        '[class.c-radio-button--disabled]': 'disabled',
        '[class.c-radio-button--prefixed]': 'isLabelPrefixed',
        '[class.is-selected]': 'checked',
        '[attr.disabled]': 'disabled',
        class: 'c-radio-button',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RadioButtonComponent),
            multi: true,
        },
    ],
})
export class RadioButtonComponent implements OnInit, ControlValueAccessor {
    public readonly id: string = `ihd-radio-button-${uniqueId++}`;
    public onChange: any;
    public onTouched: any;

    /**
     * The value to be used from the radio component.
     * @param value
     */
    @Input()
    set value(value: any) {
        if (this._value !== value) {
            this._value = value;
            if (this.radioGroup !== null) {
                if (!this.checked) {
                    // Update checked when the value changed to match the radio group's value
                    this.checked = this.radioGroup.value === value;
                }
                if (this.checked) {
                    this.radioGroup.selected = this;
                }
            }
        }
    }

    get value(): any {
        return this._value;
    }

    /**
     * The checked / selected state of the radio component.
     * @param value
     */
    @Input()
    set checked(value: boolean) {
        this.writeValue(value);
    }

    get checked(): boolean {
        return this._checked;
    }

    /**
     * Have the radio button on the right of the label pass this as true
     */
    @Input()
    isLabelPrefixed?: boolean;

    @Input()
    index?: number;

    /**
     * If this is true, then the component's selected state is determined by the value input only
     * Clicking on the component does not change the selected state and emit any output events
     */
    @Input()
    externalControlled?: boolean = false;

    /**
     * Force button to behave like a radio button and ignore de-selecting (only selection of a different button)
     * This happens automatically when assigned to a radio group.
     * @type {boolean}@memberof RadioButtonComponent
     */
    @Input()
    ignoreDeselection?: boolean = false;

    @Input()
    disabled?: boolean;

    @Output()
    select: EventEmitter<any> = new EventEmitter();

    @Output()
    change: EventEmitter<RadioChangeEvent> = new EventEmitter();

    private _value: any;
    private _checked: boolean;
    private nativeElement: HTMLElement;

    constructor(
        @Optional() public radioGroup: RadioGroupDirective,
        private element: ElementRef,
        private renderer: Renderer2,
        private cdr: ChangeDetectorRef
    ) {
        this.nativeElement = this.element.nativeElement;
    }

    ngOnInit() {
        if (this.radioGroup) {
            // If the radio is inside a radio group, determine if it should be checked
            this.checked = this.radioGroup.value === this._value;
        }
    }

    /**
     * Write a new value to the element.
     */
    writeValue(value: any) {
        const newCheckedState = coerceBooleanProperty(value);
        if (this._checked !== newCheckedState) {
            this._checked = newCheckedState;
            this.cdr.markForCheck();

            // Support radio grouping
            if (this.radioGroup) {
                if (newCheckedState && this.radioGroup.value !== this.value) {
                    this.radioGroup.selected = this;
                } else if (
                    !newCheckedState &&
                    this.radioGroup.value === this.value
                ) {
                    // When unchecking the selected radio button, update the selected radio
                    // property on the group.
                    this.radioGroup.selected = null;
                }
            }
        }
    }

    /**
     * Set the function to be called
     * when the control receives a change event.
     */
    registerOnChange(fn: (_: any) => {}) {
        this.onChange = fn;
    }

    /**
     * Set the function to be called
     * when the control receives a touch event.
     */
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    /**
     * Invoked when you click on the radio component
     *
     * @returns {void}
     * @memberof SelectCheckboxComponent
     */
    public toggle(): void {
        if (
            this.externalControlled ||
            this.disabled ||
            (this.ignoreDeselection && this.checked)
        ) {
            return;
        }

        const groupValueChanged: boolean =
            this.radioGroup && this.value !== this.radioGroup.value;
        if (this.radioGroup) {
            this.checked = true;
            if (this.radioGroup.onChange) {
                this.radioGroup.onChange(this.value);
            }

            if (groupValueChanged) {
                this.radioGroup.emitChangeEvent();
            }
        } else {
            this.checked = !this.checked;
        }

        this.change.emit(new RadioChangeEvent(this, this.value, this.checked));
        if (this.onChange) {
            this.onChange(this.checked);
        }
        if (this.onTouched) {
            this.onTouched(true);
        }
    }
}
